import { allElements, childElements, forEachElement, moveAllChildren, watchEvent } from "../util/Utils";
import type { Sheet } from "@otto-ec/global-pattern";
import { filter, filterFooter, sheetContent, sheetHeader, sheetMenuBar } from "./Selectors";
import { fireFilterSubmit, hasChanges, isForceSubmit } from "../filter/FilterFormActions";
import {
  copyLegacyLabels,
  readLegacyLabel,
  setLegacyLabels,
  updateLabel,
  updateLegacyLabel,
} from "../tracking/FeatureTracking";
import { eventQBus } from "../types/EventQBus";
import { createEmptyFilterHeaderButtons, filterHeaderButtons } from "./FilterSheetHeaderButtons";
import { FilterAccordion } from "../filter/FilterAccordion";
import { OverflowBox } from "../overflowBox/OverflowBox";
import { getActiveSheet } from "../util/NonModuleFunctions";
import type { SheetAPI } from "@otto-ec/global-pattern/types/700_Layer_und_Popup/400_pl_sheet/sheetTypes";
import { createNextBestFilterSheet, createSheetTemplate } from "./PaliSheet";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import FilterSheet from "./FilterSheet";
import { setActiveConfiguration, toggleFilterAccordion } from "@otto-ec/heureka-components/filter";
import { SvelteFacet } from "../filter/SvelteFacet";
import { resetFacetLocation, setFacetLocation } from "@otto-ec/heureka-components/facet";
import { isActive } from "../toggle/Toggle";
import { OcFilterAccordion } from "../filter/OcFilterAccordion";

const FILTER_DETAIL_SHEET_MENU_BAR_NBF_CLASS = "heureka_filterSheet__menu-bar--nbf";
const FILTER_DETAIL_SHEET_DESELECT_ALL_SELECTOR = ".ts_heureka_deselectForm";
const FILTER_DETAIL_SHEET_DESELECT_ALL_HIDDEN = "heureka_menu-bar-button--hidden";

function removeFacetChangeNothing(elem: HTMLElement, event: Event, abortWatch: AbortController) {
  const target = event.target as HTMLInputElement;
  if (target.form && (hasChanges(target.form) || isForceSubmit(target.form))) {
    updateLegacyLabel(elem, "san_FacetChangeNothing");
    updateLegacyLabel(elem, "san_FacetSearchTerm");
    updateLegacyLabel(elem, "san_FacetSearchType");
    abortWatch.abort();
  }
}

export function addFormPlChangeWatcher(form: HTMLFormElement, elem: HTMLElement, resettable = true) {
  const facetChangeNothing = readLegacyLabel(elem, "san_FacetChangeNothing");
  copyLegacyLabels(form, elem);

  if (facetChangeNothing) {
    watchEvent(
      form,
      "input",
      (event, abortWatch) => {
        copyLegacyLabels(form, elem);
        removeFacetChangeNothing(elem, event, abortWatch);
      },
      resettable ? "submit" : undefined,
      () => {
        updateLegacyLabel(elem, "san_FacetChangeNothing", facetChangeNothing);
        copyLegacyLabels(form, elem);
      },
    );
  }
}

export default class FilterDetailSheet {
  constructor(
    private readonly filterId: string,
    private readonly filterTitle?: string,
    private readonly sheet?: Sheet | SheetAPI,
    private readonly accordion?: FilterAccordion | OcFilterAccordion,
  ) {}

  public static create(filterId: string, filterTitle?: string) {
    const accordion = isActive("HEUREKA_1975_OC_COMPONENTS_MIGRATION")
      ? OcFilterAccordion.filterId(filterId)
      : FilterAccordion.filterId(filterId);
    return new FilterDetailSheet(
      filterId,
      filterTitle,
      FilterDetailSheet.newSheet(accordion, accordion.filterFooter, filterTitle),
      accordion,
    );
  }

  public static createNextBestFilter(filterId: string, filterTitle?: string) {
    const accordion = isActive("HEUREKA_1975_OC_COMPONENTS_MIGRATION")
      ? OcFilterAccordion.filterId(filterId)
      : FilterAccordion.filterId(filterId);
    const filterDetailSheet = new FilterDetailSheet(
      filterId,
      filterTitle,
      FilterDetailSheet.newNextBestFilterSheet(accordion, accordion.nextBestActionButtons, filterTitle),
      accordion,
    );
    filterDetailSheet?.sheet?.getMenuBar()?.classList.add(FILTER_DETAIL_SHEET_MENU_BAR_NBF_CLASS);
    return filterDetailSheet;
  }

  public static declare(filterId: string, filterTitle?: string) {
    return new FilterDetailSheet(filterId, filterTitle, getActiveSheet(), FilterAccordion.filterId(filterId));
  }

  private static newSheet(
    accordion?: FilterAccordion | OcFilterAccordion,
    menuSource?: HTMLElement,
    filterTitle?: string,
  ) {
    if (accordion) {
      return createSheetTemplate(accordion.filterForm, menuSource, true, filterTitle || accordion.filterId);
    }
  }

  private static newNextBestFilterSheet(
    accordion?: FilterAccordion | OcFilterAccordion,
    menuSource?: HTMLElement,
    filterTitle?: string,
  ) {
    if (accordion) {
      return createNextBestFilterSheet(accordion.filterForm, menuSource, filterTitle || accordion.filterId);
    }
  }

  setSheet() {
    const form = filter(this.filterId);
    if (form && this.sheet) {
      const footer = filterFooter(form);

      this.sheet.setTitle(this.filterTitle || this.filterId);
      this.sheet.setContent(form);
      this.sheet.setMenuBar(childElements(footer) || []);
    }
    return this;
  }

  initializeSheet() {
    const form = filter(this.filterId);
    if (form && this.sheet) {
      toggleFilterAccordion(this.filterId, true);
      setActiveConfiguration("sheetSecondLevel", this.filterId);
      setFacetLocation("second_level", this.filterId);

      OverflowBox.initAll(undefined, this.sheet.getContent());
      this.sheet.setBackButton(FilterDetailSheet.onClickDetailApply, "");
      const backButton = this.sheet.getBackButton();
      if (backButton) {
        this.prepareBackButton(backButton, form);
      }

      const content = this.sheet.getContent();
      if (content) {
        this.prepareContent(content, form);
        updateLabel(form, "san_FilterFoldoutStatus", "opened");
      }

      const source = this.accordion?.filterHeaderButtons || null;
      moveAllChildren(source, FilterDetailSheet.insertBeforeFilterHeaderButtons(this.sheet.getCloseButton()));
      FilterSheet.moveDesktopInlinedFiltersToSheet(content, false);
      this.refreshFacetChangeNothingLabel(this.sheet);
    }
    return this;
  }

  initializeNextBestFilterSheet() {
    const form = this.accordion?.filterForm;
    this.open();
    if (form && this.sheet) {
      this.sheet.setTitle(this.filterTitle || this.filterId);
      this.sheet.setContent(form);

      toggleFilterAccordion(this.filterId, true);
      setActiveConfiguration("sheetSecondLevel", this.filterId);
      setFacetLocation("nbf_sheet", this.filterId);

      const content = this.sheet.getContent();
      if (content) {
        this.prepareContent(content, form, "close_nbf", "nbf");
        updateLabel(form, "san_FilterFoldoutStatus", "opened");
      }

      this.refreshFacetChangeNothingLabel(this.sheet);
      this.sheet?.getContent()?.addEventListener("change", this.changeListener);
      this.sheet?.getContent()?.addEventListener("submit", () => {
        this.sheet?.getContent()?.classList.add("heureka_filterSheet--onSubmission");
        eventQBus.emit("ftfind.filterSheet.close");
      });
    }
    return this;
  }

  private changeListener: EventListener = () => {
    const selectedFacetValues = allElements(".js_hc_tValue:checked", sheetContent());
    if (selectedFacetValues.length === 0) {
      this.hideDeselectAllButton();
    }
  };

  private hideDeselectAllButton() {
    this.sheet
      ?.getMenuBar()
      ?.querySelector<HTMLElement>(FILTER_DETAIL_SHEET_DESELECT_ALL_SELECTOR)
      ?.classList.add(FILTER_DETAIL_SHEET_DESELECT_ALL_HIDDEN);
  }

  private restoreDeselectAllButtonVisibility(menuBar: HTMLElement) {
    menuBar
      ?.querySelector<HTMLElement>(FILTER_DETAIL_SHEET_DESELECT_ALL_SELECTOR)
      ?.classList.remove(FILTER_DETAIL_SHEET_DESELECT_ALL_HIDDEN);
  }

  private static insertBeforeFilterHeaderButtons(element?: Element | null) {
    if (element) {
      const container = createEmptyFilterHeaderButtons();
      element.insertAdjacentElement("beforebegin", container);
      return container;
    }
    return null;
  }

  private refreshFacetChangeNothingLabel(sheet: Sheet | SheetAPI) {
    forEachElement(
      ".find_filterSheet__menuBackButton[type=submit]",
      (elem) => updateLegacyLabel(elem, "san_FacetChangeNothing", "adopt_down"),
      sheet.getMenuBar(),
    );
  }

  public ifActive(callback: (sheet: Sheet | SheetAPI) => void) {
    if (this.sheet) {
      callback(this.sheet);
    }
  }

  public open() {
    if (this.sheet) {
      this.sheet.open();
    }
    return this;
  }

  public submitFilter(closeType: string) {
    FilterDetailSheet.submit(this.filterId, closeType);
    return this;
  }

  public deactivate(trackingLabel: Partial<TrackingLabels>) {
    const source = sheetContent();
    if (source) {
      const detailSheetFacet = SvelteFacet.factory.pick(undefined, source);
      detailSheetFacet?.updateFeatureStatus("visible", "loaded");
      FilterSheet.moveInlinedFiltersFrom(source, true, trackingLabel);
      delete source.dataset.filterId;
      delete source.dataset.tsLabels;
    }

    const { accordion } = this;
    if (accordion) {
      const filterHeaderButtonsContainer = filterHeaderButtons(sheetHeader());
      accordion.filterHeaderButtons = filterHeaderButtonsContainer;
      filterHeaderButtonsContainer?.remove();
      accordion.filterForm = filter(this.filterId);
      const menuBar = sheetMenuBar();
      if (menuBar && menuBar.classList.contains(FILTER_DETAIL_SHEET_MENU_BAR_NBF_CLASS)) {
        menuBar.classList.remove(FILTER_DETAIL_SHEET_MENU_BAR_NBF_CLASS);
        this.restoreDeselectAllButtonVisibility(menuBar);
        accordion.nextBestActionButtons = sheetMenuBar();
        this.sheet?.getContent()?.removeEventListener("change", this.changeListener);
      } else {
        accordion.filterFooter = sheetMenuBar();
      }
      accordion.filterFoldoutStatus = false;
    }
    /*                                       */
    resetFacetLocation();

    return this;
  }

  private prepareBackButton(backButton: HTMLElement, form: HTMLFormElement) {
    backButton.dataset.filterId = this.filterId;

    backButton.classList.add("find_filterSheet__backButton", "js_filterSheetAction");

    setLegacyLabels(backButton, {
      san_FilterFoldoutType: this.filterId,
      san_FilterFoldoutActivity: "close",
      san_FacetChangeNothing: "back",
    });

    addFormPlChangeWatcher(form, backButton);
  }

  private prepareContent(
    content: HTMLElement,
    form: HTMLFormElement,
    san_FilterFoldoutActivity: TrackingLabels["san_FilterFoldoutActivity"] = "close",
    san_FacetChangeNothing: TrackingLabels["san_FacetChangeNothing"] = "view_selection",
  ) {
    content.scrollTop = 0;
    content.dataset.filterId = this.filterId;

    setLegacyLabels(content, {
      san_FilterFoldoutType: this.filterId,
      san_FilterFoldoutActivity,
      san_FacetChangeNothing,
    });

    addFormPlChangeWatcher(form, content, false);
  }

  private static onClickDetailApply(event: Event) {
    const target = event.currentTarget as HTMLElement;
    const { filterId = "unknown", filterTitle } = target.dataset;
    FilterDetailSheet.submit(filterId, "adopt");
    eventQBus.emit("ftfind.filterSheet.closeDetail", {
      filterId,
      filterTitle,
    });
  }

  private static submit(filterId: string, filterMethod: string) {
    const elem = filter(filterId);
    if (elem instanceof HTMLFormElement) {
      elem.dataset.tsFeatureFilterMethod = filterMethod;
      fireFilterSubmit(elem);
    }
  }
}
